import { FC, useCallback, useEffect, useState } from 'react'
import { Box, Checkbox, CircularProgress, Grid, Typography } from '../../../../../../../UI'
import useStyles from '../../../styles'
import {
  getApprovalStep,
  getApprovalValues,
  getContactInEstimate,
  getContractType,
  getDocusignEnv,
  getEstimate,
  getEstimateItems,
  getTerritory,
  getUser,
  getUserPrimaryEmail,
} from '../../../../../../../../ducks/selectors'
import { useStepperContext } from '../../../../../../../../hooks/useStepperContext'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'ducks/hooks'

import TOSDocumentSign from '../../../components/TOSDocumentSign'
import BottomButtons from '../../../components/BottomButtons'
import { configActions, estimateActions, estimateItemsActions, territoriesActions } from 'ducks/actions'
import {
  APPROVAL_TYPE,
  BOSSCAT_PHONE_NUMBER,
  companyRoles,
  CONTACT_ROLE,
  formatDateToUnixTimestamp,
  history,
  isEmpty,
  round,
  USER_TYPE,
} from 'helpers/index'
import { EstimateContact } from 'ducks/types'
import { EstimateType } from 'ducks/estimate/types'

const Step3Homeowner: FC = () => {
  const dispatch = useAppDispatch()
  const classes = useStyles()
  const { id: estimateId } = history.getParamValues()

  const territory = useSelector(getTerritory())

  const { setShowWarnings, setCanGoForward } = useStepperContext()
  const [disclaimer, setDisclaimer] = useState(false)
  const [acceptingDisclaimer, setAcceptingDisclaimer] = useState(false)
  const user = useSelector(getUser)
  const estimateItems = useSelector(getEstimateItems(true))

  const fetchEstimateItems = useCallback(
    () => {
      dispatch(
        estimateItemsActions.fetchEstimateItems(
          estimateId,
          (_succ: boolean) => {
          }
        )
      )
    },
    [dispatch, estimateId]
  )

  useEffect(() => {
    fetchEstimateItems()
  }, [])

  const push = history.usePushForward()

  const estimate = useSelector(getEstimate())
  const approvalStep = useSelector(getApprovalStep)

  const disclaimerAccepted = estimate?.referralDisclaimerAcceptedOn

  const userFullName = `${user.firstName} ${user.lastName}`.trim()
  const userEmail = useSelector(getUserPrimaryEmail)

  const agentName = estimate?.properties.contacts?.find((contact) =>
    companyRoles.includes(contact.role as CONTACT_ROLE)
  )?.fullName
  const contactInEstimate = useSelector(getContactInEstimate(userEmail))
  // const docusignEnv = getDocusignEnv()
  const docusignEnv = territory?.contractors[0]?.docuSignLink || ''

  const docuSigned =
    (!isEmpty(contactInEstimate?.contractSignedOn?.toString()) &&
      estimate?.properties?.contractType === getContractType()) ||
    false

  const validation = territory?.requireContractBeforeApproval ? !!docuSigned && !!disclaimerAccepted : !!disclaimerAccepted

  const items = estimateItems?.map((item) => {
    const itemTitle = item?.title && item.title.replaceAll("&", "%26").replaceAll("?", "%3F").replaceAll("=", "%3D")
    return `${itemTitle?.length > 55 ? `${itemTitle?.slice(0, 55)}...` : itemTitle
      } - Quantity: ${item?.quantity} - $${round(item?.totalPrice)}`
  })

  useEffect(() => {
    setShowWarnings(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setCanGoForward(validation)
  }, [validation])

  const acceptDisclaimer = () => {
    setAcceptingDisclaimer(true)
    dispatch(
      estimateActions.updateEstimateByPath(
        [
          {
            op: 'add',
            path: '/referralDisclaimerAcceptedOn',
            value: formatDateToUnixTimestamp(new Date()),
          },
        ]
        ,
        (succ) => {
          setAcceptingDisclaimer(false)
          estimateActions.fetchEstimate(estimateId, (succ, estimate) => {
          })
        }
      )
    )
  }

  const handleRejectDisclaimer = () => {
    setAcceptingDisclaimer(true)
    dispatch(
      estimateActions.updateEstimateByPath(
        [
          {
            op: 'remove',
            path: '/referralDisclaimerAcceptedOn',
          }
        ]
        ,
        (succ) => {
          setAcceptingDisclaimer(false)
          estimateActions.fetchEstimate(estimateId, (succ, estimate) => {
          })
        }
      )
    )
  }

  const sendApproval = () => {
    dispatch(
      configActions.setConfigValue({ type: 'overlayLoading', value: true })
    )
    const contacts: EstimateContact[] = estimate?.properties?.contacts?.reduce(
      (acc: any, curr: Partial<EstimateContact>) => {
        if (curr.email === userEmail) {
          acc = [
            ...acc,
            {
              ...curr,
              approvalType: APPROVAL_TYPE.APPROVED,
              isPayer: true,
            },
          ]
        } else {
          acc = [...acc, curr]
        }
        return acc
      },
      []
    )
    const payload: Partial<EstimateType> = {
      id: estimate?.id,
      properties: {
        address: estimate?.properties?.address,
        accessDetails: estimate?.properties?.accessDetails,
        preferredCompletedOn: estimate?.properties?.preferredCompletedOn,
        closingDate: estimate?.properties?.closingDate,
        contacts: contacts,
        userId: user?.id,
        clientNotes: estimate?.properties?.clientNotes,
        totalValue: estimate?.properties?.totalValue ?? 0,
        approvalStep: 0,
      },
    }
    dispatch(
      estimateActions.approval(
        payload,
        (success: boolean, status: string) => {
          dispatch(
            configActions.setConfigValue({
              type: 'overlayLoading',
              value: false,
            })
          )
          if (success) {
            push(`success`)
          }
        }
      )
    )
  }

  const submitStep = () => {
    return sendApproval()
  }

  const roleToShow = [USER_TYPE.BROKER, USER_TYPE.INSTITUTIONAL].includes(
    user.clientType
  )
    ? 'Real Estate Agent'
    : user.clientType

  const handleDisclaimerChange = (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setDisclaimer(checked)
    if (checked) {
      acceptDisclaimer()
    } else {
      handleRejectDisclaimer()
    }
  }

  useEffect(() => {
    if (disclaimerAccepted) {
      setDisclaimer(true)
    }
  }, [disclaimerAccepted])

  return (
    <Grid className={classes.container} spacing={2}>
      <Grid
        container
        item
        className={classes.finalStep}
      >
        <Box display="flex" alignItems="center" flexDirection="row">
          {acceptingDisclaimer ? <CircularProgress /> : <>
            <Checkbox
              // disabled={!!disclaimerAccepted}
              checked={disclaimer}
              onChange={handleDisclaimerChange}
              color='primary'
            />
            <Typography className={classes.finalStepTextBold}>
              {`Homeview Development, LLC (“BOSSCAT”) connect you with an independent third‑party contractor (“Contractor”). Contractors are not employees, agents, or representatives of BOSSCAT. BOSSCAT does not control, supervise, guarantee, warrant, endorse, or assume responsibility for any Contractor, their work, or their decisions.

All agreements for services are solely between you and the Contractor. BOSSCAT is not a party to any such agreement. You agree that you are solely responsible for all payments to the Contractor. 

By proceeding, you authorize BOSSCAT and its affiliates to share your information as needed to facilitate the referral and you authorize the Contractor to share information with BOSSCAT. You acknowledge that the referral does not constitute professional advice or a recommendation of any Contractor. 

To the fullest extent permitted by law, you release BOSSCAT and its affiliates from any claims, losses, damages, or liabilities arising from or related to the referral, the Contractor, any acts or omissions of any Contractor, or any work performed.`}
            </Typography>
          </>}
        </Box>
      </Grid>
      {!territory?.requireContractBeforeApproval || !disclaimerAccepted ? <></> : docusignEnv ?
        <TOSDocumentSign
          onSuccess={submitStep}
          title="📋 Service Agreement"
          docuSignUrl={`${docusignEnv}&EnvelopeField_EstimateId=${estimate?.id}&Signer_UserName=${userFullName}&Signer_Email=${encodeURIComponent(userEmail)}&Fee=${round(estimate?.properties.totalWithTax || 0, 2)}&UserName=${userFullName}&UserTitle=${roleToShow}&EstimateRefId=${estimate?.properties.publicId}&AgentName=${agentName || ''}&AgentLicenseNumber=${user?.narId || '-'}&FullAddress=${estimate?.properties.fullAddress}&AddressLine1=${estimate?.properties?.address?.line_1 || estimate?.properties?.address?.line1}&AddressLine2=${(estimate?.properties.address?.line_2 || estimate?.properties.address?.line2) ? estimate?.properties.address?.line_2 || estimate?.properties.address?.line2 : `${estimate?.properties.address?.city}, ${estimate?.properties.address?.state} ${estimate?.properties.address?.zipCode}`}${estimate?.properties.address?.line_2 ? `&AddressLine3=${estimate?.properties.address?.city}, ${estimate?.properties.address?.state} ${estimate?.properties.address?.zipCode}` : ''}&Items1=${items.join(`%0A`)}`}
          docuSigned={docuSigned}
        />
        : <Grid>
          <Typography>
            No service agreement found for your territory.
          </Typography>
          <Typography>
            Please contact us at {BOSSCAT_PHONE_NUMBER} for assistance.
          </Typography>
        </Grid>}
      <BottomButtons
        buttonBack={{
          title: 'Back',
          disable: acceptingDisclaimer,
          action: () => {
            dispatch(
              configActions.setConfigValue({
                type: 'overlayLoading',
                value: true,
              })
            )

            dispatch(
              estimateActions.updateEstimateProperties(
                {
                  approvalStep: approvalStep - 1,
                },
                (succ) => {
                  dispatch(
                    configActions.setConfigValue({
                      type: 'overlayLoading',
                      value: false,
                    })
                  )
                }
              )
            )
          },
        }}
        buttonNext={{
          title: 'Schedule My Project!',
          action: submitStep,
          disable: !validation,
        }}
        showNextButton={territory?.requireContractBeforeApproval ? docuSigned : !!disclaimerAccepted}
      />
    </Grid>
  )
}

export default Step3Homeowner
